import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "./authService";
import { fetchRegistrationRequests, removeRegistrationRequest as removeRegistrationRequestApi } from "./registrationApi";
import ResetPassword from "pages/ResetPassword";

// Get user from localStorage
const user = JSON.parse(localStorage.getItem("user"));
const userToken = JSON.parse(localStorage.getItem("userToken"));

const initialState = {
  user: user ? user : null,
  isLoading: false,
  message: "",
  token: userToken || null,
  isAuthenticated: userToken ? true : false,
  resetPasswordStatus: null,
  registrationRequests: [],
  registrationRequestsLoading: false,
  registrationRequestsError: "",
  tenantTimeZone: null,
};

// Login user
export const login = createAsyncThunk("auth/login", async (user, thunkAPI) => {
  try {
    return await authService.login(user);
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const logout = createAsyncThunk("auth/logout", async () => {
  await authService.logout();
});

export const resetUserPassword = createAsyncThunk(
  "auth/resetUserPassword",
  async (userData, thunkAPI) => {
    try {
      return await authService.resetUserPassword(userData);
    } catch (error) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getRegistrationRequests = createAsyncThunk(
  "auth/getRegistrationRequests",
  async (_, thunkAPI) => {
    try {
      return await fetchRegistrationRequests();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        error?.error ||
        "Failed to load requests";
      return thunkAPI.rejectWithValue(String(message));
    }
  }
);

export const removeRegistrationRequest = createAsyncThunk(
  "auth/removeRegistrationRequest",
  async (payload, thunkAPI) => {
    try {
      const resp = await removeRegistrationRequestApi(payload);
      const uuid = typeof payload === "string" ? payload : payload?.uuid;
      return { uuid, ...resp };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reject request";
      return thunkAPI.rejectWithValue(String(message));
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.message = "";
      state.isAuthenticated = null;
    },
    isUserLoggedIn: (state) => {
      state.isAuthenticated = JSON.parse(localStorage.getItem("userToken")) ? true : false;
      state.user = JSON.parse(localStorage.getItem("user"));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.data;
        state.token = action.payload.data.access_token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload;
        state.user = null;
        state.token = null;
        state.isAuthenticated = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = null;
        state.user = null;
        state.token = null;
      })
      .addCase(resetUserPassword.pending, (state) => {
        state.isLoading = true;
        state.resetPasswordStatus = "pending";
      })
      .addCase(resetUserPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.resetPasswordStatus = "success";
        state.message = "Password reset successfully";
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.resetPasswordStatus = "failed";
        state.message = action.payload;
      })
      .addCase(getRegistrationRequests.pending, (state) => {
        state.registrationRequestsLoading = true;
        state.registrationRequestsError = "";
      })
      .addCase(getRegistrationRequests.fulfilled, (state, action) => {
        state.registrationRequestsLoading = false;
        state.registrationRequests = action.payload?.requests || [];
        state.tenantTimeZone = action.payload?.tenantTimeZone ?? null;
      })
      .addCase(getRegistrationRequests.rejected, (state, action) => {
        state.registrationRequestsLoading = false;
        state.registrationRequestsError = action.payload || "Failed to load requests";
        state.registrationRequests = [];
      })
      .addCase(removeRegistrationRequest.fulfilled, (state, action) => {
        const uuid = action.payload?.uuid;
        if (uuid) {
          state.registrationRequests = state.registrationRequests.filter((r) => r?.uuid !== uuid);
        }
      });
  },
});

export const { reset, isUserLoggedIn } = authSlice.actions;
export const selectCurrentToken = (state) => state.auth.token;
export const selectResetPasswordStatus = (state) => state.auth.resetPasswordStatus;

export default authSlice.reducer;
